/**
 * Composant CharacterBubble - Affiche un personnage avec une bulle de dialogue
 * Les personnages apparaissent aléatoirement selon l'univers choisi (Super-Héros ou Globe-Trotter)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getCurrentUniverse, 
  UNIVERSES, 
  SUPER_HERO_CHARACTERS, 
  GLOBE_TROTTER_CHARACTERS,
  getRandomQuote 
} from '../config/schools';

// Obtenir un personnage aléatoire selon le contexte
const getRandomCharacter = (context = 'general') => {
  const universe = getCurrentUniverse();
  
  if (universe === UNIVERSES.GLOBE_TROTTER) {
    const characters = GLOBE_TROTTER_CHARACTERS;
    const contextMap = {
      'grammar': [characters.archibald],
      'quiz': [characters.nestor, characters.petitXXeme, characters.lesPoliciers],
      'interview': [characters.bianca, characters.petitXXeme],
      'seo': [characters.seraphin, characters.triphon],
      'pitch': [characters.petitXXeme, characters.alcazar],
      'factcheck': [characters.lesPoliciers, characters.triphon],
      'success': [characters.petitXXeme, characters.archibald, characters.nestor],
      'error': [characters.archibald, characters.lesPoliciers],
      'general': Object.values(characters)
    };
    
    const pool = contextMap[context] || contextMap['general'];
    return pool[Math.floor(Math.random() * pool.length)];
  }
  
  // Univers Super-Héros
  const characters = SUPER_HERO_CHARACTERS;
  const contextMap = {
    'grammar': [characters.superRedChief],
    'quiz': [characters.main, characters.superFactChecker],
    'interview': [characters.superInterviewer],
    'seo': [characters.superSeoGirl],
    'pitch': [characters.superPitcher],
    'factcheck': [characters.superFactChecker],
    'success': [characters.main, characters.superFactChecker],
    'error': [characters.superRedChief, characters.main],
    'general': Object.values(characters)
  };
  
  const pool = contextMap[context] || contextMap['general'];
  return pool[Math.floor(Math.random() * pool.length)];
};

// Composant principal
function CharacterBubble({ 
  context = 'general',  // grammar, quiz, interview, seo, pitch, factcheck, success, error
  customMessage = null, // Message personnalisé (sinon citation aléatoire)
  isCorrect = null,     // true/false pour feedback, null pour général
  showAlways = false,   // Toujours afficher ou animation d'entrée
  className = '',
  size = 'medium'       // small, medium, large
}) {
  const [character, setCharacter] = useState(null);
  const [quote, setQuote] = useState('');
  const [isVisible, setIsVisible] = useState(showAlways);

  useEffect(() => {
    // Sélectionner un personnage aléatoire au montage
    const selectedContext = isCorrect === true ? 'success' : isCorrect === false ? 'error' : context;
    const char = getRandomCharacter(selectedContext);
    setCharacter(char);
    setQuote(customMessage || getRandomQuote(char));
    
    // Animation d'apparition
    if (!showAlways) {
      const timer = setTimeout(() => setIsVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, [context, customMessage, isCorrect, showAlways]);

  if (!character) return null;

  const sizeClasses = {
    small: { image: 'w-12 h-12', text: 'text-sm', bubble: 'p-3' },
    medium: { image: 'w-16 h-16', text: 'text-base', bubble: 'p-4' },
    large: { image: 'w-20 h-20', text: 'text-lg', bubble: 'p-5' }
  };

  const sizes = sizeClasses[size] || sizeClasses.medium;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.9 }}
          transition={{ type: 'spring', damping: 15 }}
          className={`speech-bubble ${sizes.bubble} ${className}`}
          data-testid="character-bubble"
        >
          <div className="flex items-start space-x-3">
            <motion.img
              initial={{ rotate: -10 }}
              animate={{ rotate: [0, -5, 5, 0] }}
              transition={{ duration: 0.5, delay: 0.2 }}
              src={character.image}
              alt={character.name}
              className={`${sizes.image} object-contain rounded-full border-3 border-black shadow-md bg-white`}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="flex-1">
              <p className="font-bold comic-text mb-1" style={{ color: '#1B4965' }}>
                {character.name} {character.altName && `(${character.altName})`} :
              </p>
              <p className={`${sizes.text} text-gray-800`}>
                {quote}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Composant pour feedback de quiz (bonne/mauvaise réponse)
export function QuizFeedbackBubble({ isCorrect, feedback, context = 'quiz' }) {
  const [character, setCharacter] = useState(null);

  useEffect(() => {
    const selectedContext = isCorrect ? 'success' : 'error';
    setCharacter(getRandomCharacter(selectedContext));
  }, [isCorrect]);

  if (!character) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="speech-bubble p-4"
      data-testid="quiz-feedback-bubble"
    >
      <div className="flex items-start space-x-3">
        <img
          src={character.image}
          alt={character.name}
          className="w-16 h-16 object-contain rounded-full border-3 border-black shadow-md bg-white"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="flex-1">
          <p className="font-bold comic-text text-lg mb-1" style={{ color: isCorrect ? '#10B981' : '#E63946' }}>
            {character.name} : {isCorrect ? 'EXCELLENT ! 🌟' : 'PAS TOUT À FAIT...'}
          </p>
          <p className="text-base text-gray-800">{feedback}</p>
        </div>
      </div>
    </motion.div>
  );
}

// Composant pour les cours magistraux (apparitions aléatoires)
export function CourseCharacterPopup({ moduleId, message, onClose }) {
  const [character, setCharacter] = useState(null);

  useEffect(() => {
    setCharacter(getRandomCharacter(moduleId));
  }, [moduleId]);

  if (!character) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, x: 100 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.8, x: 100 }}
      className="fixed bottom-4 right-4 z-50 max-w-sm"
    >
      <div className="comic-panel p-4 shadow-xl">
        <button 
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
        <div className="flex items-start space-x-3">
          <motion.img
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            src={character.image}
            alt={character.name}
            className="w-14 h-14 object-contain"
          />
          <div>
            <p className="font-bold comic-text text-sm" style={{ color: '#1B4965' }}>
              {character.name}
            </p>
            <p className="text-sm text-gray-700">
              {message || getRandomQuote(character)}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Hook pour obtenir un personnage aléatoire
export function useRandomCharacter(context = 'general') {
  const [character, setCharacter] = useState(null);

  useEffect(() => {
    setCharacter(getRandomCharacter(context));
  }, [context]);

  const refreshCharacter = () => {
    setCharacter(getRandomCharacter(context));
  };

  return { character, refreshCharacter, getQuote: () => character ? getRandomQuote(character) : '' };
}

export default CharacterBubble;
